import { DocumentTemplateField, OcrResult, OcrValue } from '../types/document-template';
import { Field } from '../types/fields';

export interface ExtractedFieldEntry {
  fieldId: string;
  name: string;
  exampleValue: any;
  description: string;
}

export interface ExtractionOptions {
  ocrResult: OcrResult;
  structureFields: Field[];
  allFieldsById: { [id: string]: Field };
  existingDescriptions: { [fieldId: string]: string };
  existingValues: { [fieldId: string]: any };
  overrideExistingValues: boolean;
}

/**
 * Processes an OcrResult (keyed by field name) against a fields map and returns
 * an array of extracted field entries ready to be added to a form.
 */
export function processOcrResult(options: ExtractionOptions): ExtractedFieldEntry[] {
  const { ocrResult, structureFields, allFieldsById, existingDescriptions, existingValues, overrideExistingValues } = options;

  // Build a reverse map from field name to Field for lookup, scoped to the structure
  const nameToField: { [name: string]: Field } = {};
  structureFields.forEach(f => {
    nameToField[f.name] = f;
  });

  const entries: ExtractedFieldEntry[] = [];

  Object.entries(ocrResult).forEach(([fieldName, extractedValue]: [string, OcrValue]) => {
    const fieldDef = nameToField[fieldName];
    if (!fieldDef) {
      console.warn(`Extracted field "${fieldName}" not found in structure fields, skipping.`);
      return;
    }
    const fieldId = fieldDef.id;

    // Preserve description from old fields if available
    const preservedDescription = existingDescriptions[fieldId] || '';

    // If override is off and field had an existing value, keep it
    const isComposed = fieldDef.type === 'object' || fieldDef.type === 'object-list';
    let finalValue: any;
    if (isComposed) {
      finalValue = transformComposedValue(extractedValue, fieldDef, allFieldsById);
    } else {
      finalValue = extractedValue != null ? String(extractedValue) : null;
    }
    if (!overrideExistingValues && existingValues[fieldId] != null) {
      finalValue = existingValues[fieldId];
    }

    entries.push({
      fieldId,
      name: fieldName,
      exampleValue: finalValue,
      description: preservedDescription
    });
  });

  return entries;
}

/**
 * Transforms an OCR composed value (object or object-list) from name-keyed raw values
 * into the ID-keyed { exampleValue } format expected by the object-field-editor component.
 *
 * OCR returns:        { "street": "123 Main", "city": "NYC" }
 * Editor expects:     { "fieldId123": { exampleValue: "123 Main" }, "fieldId456": { exampleValue: "NYC" } }
 */
function transformComposedValue(
  extractedValue: OcrValue,
  fieldDef: Field,
  allFieldsById: { [id: string]: Field }
): any {
  if (extractedValue == null) return null;

  // Build a name-to-id map for sub-fields of this composed field.
  // Sub-field ids may not be top-level structure fields, so they must be resolved
  // via the global id-keyed map; ids are globally unique so this is safe.
  const subFieldNameToId: { [name: string]: string } = {};
  if (fieldDef.fieldIds) {
    for (const subId of fieldDef.fieldIds) {
      const subField = allFieldsById[subId];
      if (subField) {
        subFieldNameToId[subField.name] = subId;
      }
    }
  }

  if (fieldDef.type === 'object-list' && Array.isArray(extractedValue)) {
    return extractedValue.map(item => transformObjectItem(item, subFieldNameToId));
  }

  if (fieldDef.type === 'object' && typeof extractedValue === 'object' && !Array.isArray(extractedValue)) {
    return transformObjectItem(extractedValue as Record<string, any>, subFieldNameToId);
  }

  // Fallback: return as-is if shape is unexpected
  return extractedValue;
}

function transformObjectItem(
  item: Record<string, any>,
  subFieldNameToId: { [name: string]: string }
): { [fieldId: string]: DocumentTemplateField } {
  const result: { [fieldId: string]: DocumentTemplateField } = {};
  for (const [key, val] of Object.entries(item)) {
    const fieldId = subFieldNameToId[key];
    if (fieldId) {
      result[fieldId] = { exampleValue: val ?? null };
    }
  }
  return result;
}
